import type { HttpClient } from './http-client.types';

const PROTECTED_FIELDS = ['id', '_id', 'workspaceId'];

export function getProjects(http: HttpClient, params: Record<string, string | number> = {}) {
  if (!params || Object.keys(params).length === 0) return http.get('/collections');
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return http.get(qs ? `/collections?${qs}` : '/collections');
}

export function getProject(http: HttpClient, projectId: string) {
  return http.get(`/collections/${projectId}`);
}

export function createProject(http: HttpClient, projectData: unknown) {
  // No retry: a create whose response was lost would come back as a second
  // collection (the name index never fires at the workspace root).
  return http.request({ method: 'POST', url: '/collections', data: projectData }, 0);
}

export function patchProject(http: HttpClient, projectId: string, field: string, value: unknown) {
  if (PROTECTED_FIELDS.includes(field)) {
    console.error(`Attempted to update protected field: ${field}`);
    return Promise.resolve({
      success: false as const,
      error: { code: 'PROTECTED_FIELD', message: `Cannot update protected field: ${field}` },
    });
  }
  return http.patch(`/collections/${projectId}`, { [field]: value });
}

export function bulkDeleteProjects(http: HttpClient, projectIds: string[]) {
  // retries: 0 — a bulk delete is a non-idempotent destructive POST. The
  // client's default retry replays it on 5xx AND timeout/refused; a replayed
  // delete finds every id already trashed and returns a 409 with ALL ids in
  // error.details, so the UI reports "Moved 0 of N" while everything was
  // actually deleted (PR #492 review).
  return http.request({ method: 'POST', url: '/collections/bulk-delete', data: { projectIds } }, 0);
}

/** Suggested collections the caller can see, and whether to offer them (decision 0018). */
export function getPurposeCollections(http: HttpClient) {
  return http.get('/collections/purpose-collections');
}

/** Accept ('setup') or dismiss the suggested collections offer. No retry: setup creates. */
export function closePurposeCollectionsOffer(http: HttpClient, outcome: 'setup' | 'dismiss') {
  return http.request(
    { method: 'POST', url: '/collections/purpose-collections/offer', data: { outcome } },
    0,
  );
}
